import React, { useEffect, useState, useRef } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, MenuItem, Select, InputLabel, FormControl, Avatar, InputAdornment, IconButton, makeStyles, Typography, Box } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { useTranslations } from 'examsbook-react-sdk/custom-hooks';
import { ReactComponent as TrophyIcon } from "../../assets/images/trophy.svg";
import { getLeaderboard } from 'examsbook-react-sdk/actions/getLeaderboard';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '../../constants/themeProvider';
import { useTheme as useMuiTheme } from "@material-ui/core/styles";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles({
    menuPaper: {
        top: '146px !important',
        "@media (max-width: 767px)": {
            top: '135px !important',
            left: '9px !important',
        }
    },
    menuPaperSortBy : {
        top: '146px !important',
         "@media (max-width: 767px)": {
            top: '300px !important',
            left: '9px !important',
        }
    },
    youLabel: {
        backgroundColor: '#0C3450',
        color: '#fff',
        fontWeight: 'bold',
        padding: '2px 6px',
        borderRadius: '100px',
        marginRight: '5px',
        fontSize: '12px',
    },
    leaderBoardContainer: {
        padding: '20px',
        background: '#fff',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(88vh - 0px)',
        maxHeight: 'calc(88vh - 0px)',
        overflowY: 'auto',
        "@media (max-width: 959px)": {
            paddingBottom: '80px !important',
        }
    },
    leaderBoardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px',
        alignItems: 'center'
    },
    leaderBoardFilterBox: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
    },
    decreaseSpace: {
        padding: '6px !important'
    },
});

function getSortByDisplayLabel(selected, t) {
    const sortLabels = {
        name_asc: t('Name (A-Z)'),
        name_desc: t('Name (Z-A)'),
        rank_asc: t('Rank (ASC)'),
        rank_desc: t('Rank (DESC)'),
        points_asc: t('Points (ASC)'),
        points_desc: t('Points (DESC)'),
    };

    return sortLabels[selected] || t('Name (A-Z)');
}

function LeaderBoard() {
    const classes = useStyles();
    const theme = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [userCourses, setUserCourses] = useState([]);
    const user = useSelector((state) => state.user);
    const userId = user?.user?._id ? user?.user?._id : user?._id;
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [sortBy, setSortBy] = useState("rank_asc");
    const [search, setSearch] = useState('');
    const t = useTranslations();
    const limit = 10;
    const [hasMoreData, setHasMoreData] = useState(true);
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const [expandedRow, setExpandedRow] = useState(null);
    const currentRequestRef = useRef({ courseId: null, search: null, sortBy: null });
    const isFetchingRef = useRef(false);
    const pathParts = location?.pathname?.split('/') || [];
    const courseIdFromPath = pathParts.length > 0 ? pathParts[pathParts.length - 1] : null;
    const courseNameFromApi = userCourses?.find(row => row?.courseId === courseIdFromPath);
    const courseId = location?.state?.params?.courseId ? location?.state?.params?.courseId : courseIdFromPath;
    const courseName = location?.state?.params?.courseName ? location?.state?.params?.courseName : courseNameFromApi?.name;
    const [selectedCourse, setSelectedCourse] = useState({ id: courseId, name: courseName });

    const handleScroll = (event) => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight && hasMoreData && !isFetching && !isInitialLoad && leaderboardData.length > 0) {
            const skip = leaderboardData?.length || 0;
            getLeaderBoardData(skip);
        }
    };

    useEffect(() => {
        setLeaderboardData([]);
        setHasMoreData(true);
        setIsInitialLoad(true);
        isFetchingRef.current = false;
        currentRequestRef.current = { courseId: selectedCourse.id, search, sortBy };
        getLeaderBoardData(0);
    }, [selectedCourse.id, search, sortBy]);

    useEffect(() => {
        if (courseId && courseId !== selectedCourse.id) {
            setSelectedCourse({ id: courseId, name: courseName });
        }
    }, [courseId, courseName]);

    const getLeaderBoardData = async (skip = 0) => {
        if (isFetchingRef.current) {
            return;
        }

        try {
            isFetchingRef.current = true;
            setIsFetching(true);
            const requestCourseId = selectedCourse.id;
            const requestSearch = search;
            const requestSortBy = sortBy;            
            await dispatch(getLeaderboard(limit, skip, requestSearch, requestCourseId, requestSortBy)).then((res) => {
                const isStaleRequest = 
                    currentRequestRef.current.courseId !== requestCourseId ||
                    currentRequestRef.current.search !== requestSearch ||
                    currentRequestRef.current.sortBy !== requestSortBy;
                
                if (isStaleRequest) {
                    isFetchingRef.current = false;
                    setIsFetching(false);
                    return;
                }
                
                const newData = res?.userList || [];
                if (skip === 0) {
                    setLeaderboardData(newData);
                    setIsInitialLoad(false);
                } else {
                    setLeaderboardData(prev => {
                        const existingIds = new Set(prev.map(user => user._id));
                        const filteredData = newData.filter(row => !existingIds.has(row._id));
                        return [...prev, ...filteredData];
                    });
                }
                setUserCourses(res?.userCourses);
                if (newData.length === 0 || newData.length < limit) {
                    setHasMoreData(false);
                }
                setIsLoading(true);
                isFetchingRef.current = false;
                setIsFetching(false);
            });
        } catch (error) {
            console.log("Error -", error.message);
            setLeaderboardData([]);
            isFetchingRef.current = false;
            setIsFetching(false);
        }
    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const handleClearSearch = () => {
        setSearch('');
    };

    const handleCourseChange = (event) => {
        const value = event.target.value;
        setSelectedCourse({ id: value.id, name: value.name });
        if (value.id) {
            history.push({ pathname: `${value.id}`, state: { params: { courseId: value.id, courseName: value.name }}});
        }
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    const displayData = leaderboardData || [];
    const hasRank = displayData.some(row => row.rank !== undefined && row.rank !== null);
    const hasName = displayData.some(row => row.name);
    const hasPoints = displayData.some(row => row.totalAwardedPoints !== undefined && row.totalAwardedPoints !== null);
    const hasEmail = displayData.some(row => row.email);
    const hasPhone = displayData.some(row => row.mobileNumber);
    const hasLevel = displayData.some(row => row.level);

    return (
        <Box className={classes.leaderBoardContainer} style={isMobile ? { padding: 8 } : {}} onScroll={handleScroll}>
            <Box style={{ flex: '1 0 auto' }}>
                <Box className={classes.leaderBoardHeader} style={isMobile ? { flexDirection: 'column', alignItems: 'stretch', gap: 12 } : {}}>
                    <FormControl variant="outlined" style={{ minWidth: isMobile ? 180 : 250, marginBottom: isMobile ? 12 : 0 }}>
                        <InputLabel>{t('Courses')}</InputLabel>
                        <Select value={selectedCourse} onChange={handleCourseChange} label={t('Courses')} MenuProps={{ classes: { paper: classes.menuPaper } }} renderValue={(selected) => selected.name}>
                          {userCourses && userCourses.length > 0 ? (
                            userCourses.map((course) => (
                              <MenuItem key={course.courseId} value={{ id: course.courseId, name: course.name }}>
                                {course.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>{t('No courses found')}</MenuItem>
                          )}
                        </Select>
                    </FormControl>
                    <Box className={classes.leaderBoardFilterBox} style={isMobile ? { flexDirection: 'column', gap: 12, alignItems: 'stretch' } : {}}>
                        <TextField variant="outlined" placeholder={t('Search')} style={{ width: isMobile ? '100%' : '300px' }} value={search} onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl variant="outlined" style={{ minWidth: isMobile ? 140 : 190, marginTop: isMobile ? 16 : 0 }}>
                            <InputLabel>{t('Sort By')}</InputLabel>
                            <Select
                                value={sortBy}
                                onChange={handleSortChange}
                                label={t('Sort By')}
                                displayEmpty
                                MenuProps={{ classes: { paper: classes.menuPaperSortBy } }}
                                renderValue={(selected) => getSortByDisplayLabel(selected, t)}
                            >
                                <MenuItem value="name_asc">{t('Name (A-Z)')}</MenuItem>
                                <MenuItem value="name_desc">{t('Name (Z-A)')}</MenuItem>
                                <MenuItem value="rank_asc">{t('Rank (ASC)')}</MenuItem>
                                <MenuItem value="rank_desc">{t('Rank (DESC)')}</MenuItem>
                                <MenuItem value="points_asc">{t('Points (ASC)')}</MenuItem>
                                <MenuItem value="points_desc">{t('Points (DESC)')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <TableContainer component={Paper} style={isMobile ? { overflowX: 'auto' } : {}}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        {isLoading && Array.isArray(displayData) && displayData.length > 0 && (
                            <TableHead>
                                <TableRow>
                                    {hasRank && <TableCell>{t('rank')}</TableCell>}
                                    {hasName && <TableCell>{t('name')}</TableCell>}
                                    {isMobile && hasPoints && <TableCell>{t('Points')}</TableCell>}
                                    {!isMobile && hasEmail && <TableCell>{t('Email')}</TableCell>}
                                    {!isMobile && hasPhone && <TableCell>{t('Phone no')}</TableCell>}
                                    {!isMobile && hasPoints && <TableCell>{t('Points')}</TableCell>}
                                    {!isMobile && hasLevel && <TableCell style={{textTransform: 'capitalize'}}>{t('Level')}</TableCell>}
                                    {(isMobile || (window.innerWidth > 600 && window.innerWidth <= 959)) && <TableCell>{t('Action')}</TableCell>}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {isLoading && Array.isArray(displayData) && displayData.length > 0 ? (
                                displayData.map((row, index) => (
                                    <React.Fragment key={row._id}>
                                        <TableRow onClick={() => setExpandedRow(expandedRow === row._id ? null : row._id)}>
                                            {hasRank && (
                                                <TableCell className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} align={isMobile && window.innerWidth < 600 ? 'center' : undefined}>
                                                  <Box style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: isMobile && window.innerWidth < 600 ? 'center' : undefined }}>
                                                    {row.rank !== undefined ? row.rank : ''}
                                                    {row.rank === 1 && (
                                                        <TrophyIcon style={{ width: '20px', height: '20px', color: '#FFD700' }} />
                                                    )}
                                                  </Box>
                                                </TableCell>
                                            )}
                                            {hasName && (
                                                <TableCell className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} align={isMobile && window.innerWidth < 600 ? 'center' : undefined}>
                                                  <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: isMobile && window.innerWidth < 600 ? 'left' : undefined, position: isMobile && 'relative', left: isMobile && '10px' }}>
                                                    <Avatar src={row.image} alt={row.name} style={{ border: `1px solid ${theme?.primaryColor}`, backgroundColor: !row.image ? theme?.primaryColor : undefined }} />
                                                    <Typography variant="span" style={{display: 'contents'}}>
                                                        {row.name}
                                                        {row?._id === userId && (
                                                            <Typography variant="span" className={classes.youLabel}>
                                                                {t('You')}
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                            )}
                                            {isMobile && hasPoints && (
                                                <TableCell className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} align={isMobile && window.innerWidth < 600 ? 'center' : undefined}>
                                                    <Typography variant="span">{row.totalAwardedPoints}</Typography>
                                                </TableCell>
                                            )}
                                            {!isMobile && hasEmail && (
                                                <TableCell className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} align={isMobile && window.innerWidth < 600 ? 'center' : undefined}>
                                                    <Typography variant="span">{row.email}</Typography>
                                                </TableCell>
                                            )}
                                            {!isMobile && hasPhone && (
                                                <TableCell>{row.mobileNumber}</TableCell>
                                            )}
                                            {!isMobile && hasPoints && (
                                                <TableCell>{row.totalAwardedPoints}</TableCell>
                                            )}
                                            {!isMobile && hasLevel && (
                                                <TableCell>{row.level}</TableCell>
                                            )}
                                            {(isMobile || (window.innerWidth > 600 && window.innerWidth <= 959)) && (
                                              <TableCell className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} align={isMobile && window.innerWidth < 600 ? 'center' : undefined}>
                                                <IconButton aria-label="expand row" size="small">
                                                  <ExpandMoreIcon style={{ transform: expandedRow === row._id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                                </IconButton>
                                              </TableCell>
                                            )}
                                        </TableRow>
                                        {(isMobile || (window.innerWidth > 600 && window.innerWidth <= 959)) && expandedRow === row._id && (
                                            <TableRow>
                                                <TableCell colSpan={
                                                    (isMobile
                                                        ? [hasRank, hasName, hasPoints].filter(Boolean).length + 1 // +1 for Action column
                                                        : [hasRank, hasName, hasEmail, hasPhone, hasPoints, hasLevel].filter(Boolean).length + 1)
                                                } className={isMobile && window.innerWidth < 600 ? classes.decreaseSpace : undefined} style={{ padding: 0, background: '#f5f5f5', textAlign: 'center', borderBottom: 0 }}>
                                                    <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 8, padding: '10px', width: '100%' }}>
                                                        {hasEmail && (
                                                            <Box style={{ flex: 1, textAlign: 'center' }}>
                                                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}><b>{t('Email')}</b></Typography>
                                                                <Typography variant="span" style={{ marginBottom: 8, lineBreak: 'anywhere' }}>{row.email}</Typography>
                                                            </Box>
                                                        )}
                                                        {hasPhone && (
                                                            <Box style={{ flex: 1, textAlign: 'center' }}>
                                                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}><b>{t('Phone no')}</b></Typography>
                                                                <Typography variant="span" style={{ marginBottom: 8 }}>{row.mobileNumber}</Typography>
                                                            </Box>
                                                        )}
                                                        {hasLevel && (
                                                            <Box style={{ flex: 1, textAlign: 'center' }}>
                                                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}><b>{t('Level')}</b></Typography>
                                                                <Typography variant="span">{row.level}</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : isLoading && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        {t('No record found')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}

export default LeaderBoard;